/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * AssignRoleModal
 *
 * Props:
 * - open: boolean (show/hide)
 * - onClose: function() → called when Cancel or close (X) is clicked
 * - applicationId: string | number (from route)
 * - user: the selected application user (must contain id, username, email, and optional role)
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import ProgressBarDialog from '../common/ProgressBar';
import AlertDialog from '../common/AlertDialog';
import Button from '../Button';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

function AssignRoleModal({ open, onClose, applicationId, user }) {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');

  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [alertSource, setAlertSource] = useState(null); // 'load' | 'save' | null

  useEffect(() => {
    if (!open || !user) return;
    const currentRoleId = user.role?.id ?? null;
    setSelectedRoleId(currentRoleId);
  }, [open, user]);

  // Fetch roles whenever modal opens or applicationId changes
  useEffect(() => {
    if (!open || !applicationId) return;

    let isCancelled = false;

    async function fetchRoles() {
      setRolesLoading(true);
      setRolesError('');

      // progress bar for roles loading
      setProgressMessage('Loading roles...');
      setProgressOpen(true);

      try {
        const resp = await apiHelper.apiCall(`/arcee/user/roles/${applicationId}/?page_size=1000`, {
          method: 'GET',
        });

        if (!resp.ok) {
          let errBody;
          try {
            errBody = await resp.json();
          } catch {
            errBody = await resp.text().catch(() => null);
          }
          if (isCancelled) return;

          const msg =
            typeof errBody === 'string'
              ? errBody
              : 'Failed to load roles.';

          setRoles([]);
          setRolesError(msg);

          setProgressOpen(false);
          setAlertTitle('Failed to load roles');
          setAlertMessage(msg);
          setAlertType('error');
          setAlertSource('load');
          setAlertOpen(true);
          return;
        }

        const data = await resp.json().catch(() => []);
        if (isCancelled) return;

        const list = Array.isArray(data) ? data : (data.results ?? []);
        setRoles(list);
        setRolesError('');
      } catch (err) {
        if (isCancelled) return;

        const msg = err.message || 'Could not load roles.';
        setRolesError(msg);
        setRoles([]);

        setProgressOpen(false);
        setAlertTitle('Failed to load roles');
        setAlertMessage(msg);
        setAlertType('error');
        setAlertSource('load');
        setAlertOpen(true);
        return;
      } finally {
        if (!isCancelled) {
          setRolesLoading(false);
          setProgressOpen(false);
        }
      }
    }

    fetchRoles();

    return () => {
      isCancelled = true;
    };
  }, [open, applicationId]);

  if (!open) return null;

  const handleCheckboxChange = (roleId) => {
    setSelectedRoleId((prev) => (prev === roleId ? null : roleId));
  };

  const formatServerErrors = (errBody) => {
    if (!errBody) return 'Something went wrong. Please try again.';
    if (typeof errBody === 'string') return errBody;
    if (typeof errBody === 'object') {
      const parts = [];
      for (const [k, v] of Object.entries(errBody)) {
        if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
        else parts.push(`${k}: ${String(v)}`);
      }
      return parts.join('\n');
    }
    return 'Something went wrong. Please try again.';
  };

  const handleSave = async () => {
    if (!user || !user.id || !applicationId) {
      setAlertTitle('Invalid Data');
      setAlertMessage('User or application information is missing.');
      setAlertType('error');
      setAlertSource('save');
      setAlertOpen(true);
      return;
    }

    setSaving(true);
    setProgressMessage('Saving role assignment...');
    setProgressOpen(true);

    const bodyPayload = {
      application_user_id: user.id,
      role_id: selectedRoleId,
    };

    try {
      const resp = await apiHelper.apiCall('/arcee/user/roles/assign/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload),
      });

      setProgressOpen(false);

      if (!resp.ok) {
        let errBody;
        try {
          errBody = await resp.json();
        } catch {
          errBody = await resp.text().catch(() => null);
        }

        const message = formatServerErrors(errBody);
        setAlertTitle('Failed to update role');
        setAlertMessage(message);
        setAlertType('error');
        setAlertSource('save');
        setAlertOpen(true);
        return;
      }

      const data = await resp.json().catch(() => ({}));
      const successMsg =
        data.message ||
        (selectedRoleId
          ? 'Role assigned successfully.'
          : 'Role removed successfully.');

      setAlertTitle('Success');
      setAlertMessage(successMsg);
      setAlertType('success');
      setAlertSource('save');
      setAlertOpen(true);
    } catch (err) {
      setProgressOpen(false);
      setAlertTitle('Network Error');
      setAlertMessage(err.message || 'Unable to connect to server. Please try again later.');
      setAlertType('error');
      setAlertSource('save');
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);

    // If error came from loading roles → close modal
    if (alertSource === 'load') {
      setAlertSource(null);
      onClose && onClose();
      return;
    }

    // After successful save, redirect to users list and reload
    if (alertType === 'success' && alertSource === 'save') {
      if (applicationId) {
        navigate(`/dashboard/applications/${applicationId}/users`);
      } else {
        navigate('/dashboard/applications');
      }
      window.location.reload();
    }

    // reset for next time
    setAlertSource(null);
  };

  const handleCancel = () => {
    if (saving) return; // optionally block cancel while saving
    onClose && onClose();
  };

  // Simple centered modal styling
  const backdropStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1050,
  };

  const modalStyle = {
    width: 480,
    maxWidth: '95vw',
    background: '#fff',
    borderRadius: theme.cardRadius,
    boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
    padding: '1.5rem 1.75rem 1.25rem',
    position: 'relative',
  };

  return (
    <>
      <div style={backdropStyle} onClick={handleCancel}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0" style={{ color: theme.primary, fontWeight: 600 }}>
              Assign Role
            </h5>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 20,
                lineHeight: 1,
                color: theme.primary
              }}
              onMouseEnter={(e) => (e.target.style.color = theme.secondary)}
              onMouseLeave={(e) => (e.target.style.color = theme.primary)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* User info */}
          {user && (
            <div style={{ marginBottom: 10, fontSize: 14 }}>
              <div>
                <strong>User:</strong> {user.username} ({user.email})
              </div>
            </div>
          )}

          {/* Roles list */}
          <div
            style={{
              maxHeight: 260,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 10,
              padding: '8px 10px',
              marginBottom: 12,
            }}
          >
            {!rolesLoading && roles.length === 0 && !rolesError && (
              <div style={{ padding: '10px 4px', fontSize: 14, color: '#777' }}>
                No roles found for this application.
              </div>
            )}

            {roles.length > 0 &&
              roles.map((role) => (
                <div
                  key={role.id}
                  className="d-flex align-items-start"
                  style={{
                    padding: '6px 4px',
                    borderBottom: '1px solid #f2f2f2',
                  }}
                >
                  <div className="form-check" style={{ marginRight: 8 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`role-${role.id}`}
                      checked={selectedRoleId === role.id}
                      onChange={() => handleCheckboxChange(role.id)}
                      style={{ cursor: 'pointer' }}
                      disabled={saving || rolesLoading}
                    />
                  </div>
                  <label
                    htmlFor={`role-${role.id}`}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14, color: theme.primary }}>
                      {role.name}
                    </div>
                    {role.description && (
                      <div style={{ fontSize: 13, color: '#555' }}>
                        {role.description}
                      </div>
                    )}
                  </label>
                </div>
              ))}
          </div>



          {/* Hint for unassigning */}
          <div style={{ fontSize: 12, color: '#777', marginBottom: 10 }}>
            You can leave all checkboxes unchecked to remove this user's role.
          </div>

          {/* Footer buttons */}
          <div className="d-flex justify-content-end" style={{ gap: 10 }}>
            <Button
              mode="outlined"
              type="button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              mode="primary-bold"
              type="button"
              onClick={handleSave}
              disabled={saving || rolesLoading}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Progress while loading roles / saving */}
      <ProgressBarDialog
        open={progressOpen}
        text_message={progressMessage}
        cancellable={false}
      />

      {/* Alert for success / error */}
      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={handleAlertClose}
        onCancel={() => setAlertOpen(false)}
        mini_mode={false}
      />
    </>
  );
}

export default AssignRoleModal;